<template>
  <VContainer
    data-testid="search"
    class="pa-0"
  >
    <VRow
      no-gutters
      class="py-0"
    >
      <VCol
        v-on-click-outside="reset"
        cols="auto"
        data-testid="search-form"
        class="d-flex flex-grow-1 align-center px-0"
      >
        <VAutocomplete
          v-model="suggestionModel"
          v-model:search="term"
          data-testid="search-placeholder"
          maxlength="50"
          :placeholder="$t('input.btn.search')"
          :aria-label="$t('input.btn.search')"
          persistent-placeholder
          :no-data-text="noDataText"
          :items="suggestedTerms"
          :loading="loading"
          :hide-no-data="hideNoData"
          :menu-props="{ maxHeight: 550, attach: true, location: 'bottom start' }"
          no-filter
          variant="outlined"
          density="compact"
          menu-icon=""
          hide-details
          full-width
          rounded
          bg-color="backgroundDiv"
          color="backgroundDiv"
          clearable
          @update:model-value="searchBySuggestion"
          @keyup.enter="searchByTerm(term)"
          @click:append="term=''"
        >
          <template #clear>
            <!-- ONE key, not two concatenated. Joining input.btn.clear with
                 input.btn.search assumed English word order and rendered
                 "Effacer Rechercher" (fr-ca) / "Löschen Suchen" (de-de) — two
                 bare infinitives, not a name describing an action. A richer
                 "Clear search term" needs a new Lokalise key; until one exists a
                 single existing key is grammatical in every locale, which
                 matters more here than being maximally descriptive. -->
            <button
              v-if="term"
              type="button"
              class="icon-btn"
              data-testid="search-icon"
              :aria-label="$t('input.btn.clear')"
              @click.stop="term=undefined"
            >
              <VIcon aria-hidden="true" color="greyedOut">
                {{ icons.mdiClose }}
              </VIcon>
            </button>
          </template>
          <template #append-inner>
            <button
              type="button"
              class="icon-btn"
              data-testid="search-icon"
              :aria-label="$t('label.searchProducts')"
              @click.stop="searchByTerm(term)"
            >
              <VIcon aria-hidden="true" color="greyedOut">
                {{ icons.mdiMagnify }}
              </VIcon>
            </button>
          </template>
          <!-- Vuetify 4 passes the raw suggestion as `item` (`internalItem` is the
               ListItem object, which rendered "[object Object]"). The VListItem with
               the slot's props is what makes a row clickable, keyboard-pickable and an
               aria option. AB#64561 -->
          <template #item="{ props: itemProps, item }">
            <VListItem v-bind="itemProps" title="">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span v-html="item" />
            </VListItem>
          </template>
          <!-- Left blank, as in nuxt2: the field shows the typed term, never the
               picked suggestion with its <em> highlight markup. AB#64561 -->
          <template #selection />
        </VAutocomplete>
      </VCol>
    </VRow>
  </VContainer>
</template>

<script lang="ts" setup>
import { vOnClickOutside } from '@vueuse/components'
import { mdiMagnify, mdiClose } from '@mdi/js'
import { useShopStore } from '~/stores/shop'
import { CatalogProductsAPIOrderBy } from '~/types/shop'


const MIN_QUERY_TEXT_LENGTH = 3
const icons = { mdiMagnify, mdiClose }

const emits = defineEmits<{
  (e: 'reset'): void
}>()

const i18n = useI18n()
const route = useRoute()
const router = useRouter()
const shopStore = useShopStore()

const term = ref()
const loading = ref(false)
// Used as a placeholder to handle v-autocomplete issue concerning the v-model being undefined
const suggestionModel = ref<string | null>(null)

const suggestions = computed(() => shopStore.suggestedTerms ?? [])
const noDataText = computed(() => {
  return !term.value || term.value?.length < MIN_QUERY_TEXT_LENGTH
    ? i18n.t('label.searchTooShort', { size: MIN_QUERY_TEXT_LENGTH })
    : i18n.t('label.noResults')
})
const { isDemo } = useSAuthState()
const hideNoData = computed(() => {
  return isDemo.value || !term.value || loading.value || suggestedTerms.value?.length > 0
})
const suggestedTerms = computed(() => {
  return !loading.value && term.value?.length >= MIN_QUERY_TEXT_LENGTH && suggestions.value.length
    ? suggestions.value.map((s: string) => s.replace(regexp(term.value), '<em>$1</em>'))
    : []
})

watch(route, () => {
  if (term.value) {
    reset()
  }
})
watchDebounced(term, async () => {
  if (term.value?.length >= MIN_QUERY_TEXT_LENGTH) {
    loading.value = true
    await shopStore.queryAutoComplete(term.value)
    loading.value = false
  } else if (suggestions.value?.length > 0) {
    shopStore.suggestedTerms = []
  }
},
{ debounce: 350 })

const regexp = (term: string) => {
  //   /**
  //    * Following expression replaces all regex-unsafe values from `this.term`
  //    * so it can be used as regexp for case-insensitive global replace
  //    *
  //    * Example:
  //    * this.term = 'a.b'
  //    * this.regexp = /a\.b/ig
  //    */
  return new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig')
}
const display = useDisplay()
const searchByTerm = (term: string | null) => {
  reset()
  if (term) {
    // smAndDown is a ref; read as a bare object it was always truthy. nuxt2 sends
    // 30 on small screens. AB#64561
    const pageSize = display.smAndDown.value ? '30' : undefined
    const searchPath = '/search'
    shopStore.clearProducts()
    shopStore.clearContentPages()
    router.push({ path: `${searchPath}/${term}`, query: { sort: CatalogProductsAPIOrderBy.Relevance, ts: Date.now().toString(), pageSize } })
  }
}
const searchBySuggestion = () => {
  const term = suggestionModel.value?.replace(/<\/?em>/g, '') ?? null
  // Cleared so picking the same suggestion again is a change, and fires. AB#64561
  suggestionModel.value = null
  searchByTerm(term)
}

const reset = () => {
  emits('reset')
  term.value = ''
}

// for tests
defineExpose({ suggestedTerms, reset })
</script>

<style lang="scss" scoped>
:deep(.v-list) {
  padding: 0;
}
:deep(.v-field__append-inner) {
  padding-left: 10px;
}
:deep(em) {
  font-style: normal;
  font-weight: bold;
  color: rgb(var(--v-theme-primary));
}
:deep(.v-field__outline__start) {
  flex-basis: 20px;
}
:deep(.v-input--density-compact) {
  --v-input-padding-top: 4px;
}
</style>
